import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import Footer from "src/components/Footer";
import Header from "src/components/Header";
import { getErrorMsg } from "src/helpers/getErrorMsg";
import { colors } from "src/styles";
import { debugError } from "src/utils/log";
import { WashroomLogsInputForm } from "./InputForm";
import { FirebaseDataService } from "src/services/Firebase/data";
import { Employee, LocationSpecificWashroomLogsForTable } from "src/types";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { logInUser } from "src/redux/actions/user";
import { MAXIMO_TIME_ZONE } from "src/config/сonstants";
import FirebaseStorageService from "src/services/Firebase/storage";
import useLocation from "src/hooks/useLocation";

interface WashroomLogsFormProps {
  name: string;
  logo: string;
  setName: (name: string) => void;
  setLogo: (logo: string) => void;
}

export const WashroomLogsForm: React.FC<WashroomLogsFormProps> = ({ name, logo, setName, setLogo }) => {
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    reset,
    formState: { isValid, errors },
  } = useForm({
    mode: "all",
  });

  const dispatch = useDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [labelMessage, setLabelMessage] = useState<string>("");
  const [employeeData, setEmployeeData] = useState<Employee | null>(null);
  const [validationError, setValidationError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [isMetroLinxClient, setIsMetroLinxClient] = useState(false);
  const [isCityOfMarkhamClient, setIsCityOfMarkhamClient] = useState(false);

  const navigate = useNavigate();

  const urlparams = useParams();

  const { coords: userLocation } = useLocation();

  const getDistance = (
    coord1: { latitude: number; longitude: number },
    coord2: { latitude: number; longitude: number },
  ) => {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371e3; // metres
    const φ1 = toRad(coord1.latitude);
    const φ2 = toRad(coord2.latitude);
    const Δφ = toRad(coord2.latitude - coord1.latitude);
    const Δλ = toRad(coord2.longitude - coord1.longitude);
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const validateDistance = (
    userLocation: { lat: string; long: string } | { latitude: number; longitude: number } | null,
    locationPosition: { lat: string; long: string } | { latitude: number; longitude: number } | null,
  ) => {
    if (!userLocation || !locationPosition) {
      return;
    }

    const normalize = (loc: any) => {
      if ("lat" in loc && "long" in loc) {
        return { lat: parseFloat(loc.lat), long: parseFloat(loc.long) };
      }
      if ("latitude" in loc && "longitude" in loc) {
        return { lat: Number(loc.latitude), long: Number(loc.longitude) };
      }
      return { lat: NaN, long: NaN };
    };

    const { lat: userLat, long: userLong } = normalize(userLocation);
    const { lat: locationLat, long: locationLong } = normalize(locationPosition);

    if (Number.isNaN(userLat) || Number.isNaN(userLong) || Number.isNaN(locationLat) || Number.isNaN(locationLong)) {
      return;
    }

    const distance = getDistance(
      { latitude: userLat, longitude: userLong },
      { latitude: locationLat, longitude: locationLong },
    );

    if (distance > 500) {
      // distance in meters
      // navigate("/404");
      navigate("/error?message=You%20may%20only%20add%20logs%20at%20work%20location");
      setValidationError("You are too far from the location");
    }
  };

  // Check if washroom exists
  useEffect(() => {
    const validateWashroom = async () => {
      if (urlparams?.locationId && urlparams?.washroomId) {
        setLoading(true);
        try {
          const response = await FirebaseDataService.getLocationById(urlparams.locationId);

          for (const washroom of Object.values(response.data.washrooms || {})) {
            if (washroom.id === urlparams.washroomId) {
              setLocationName(`${response.data.label}, ${washroom.label}`);
              break;
            }
          }

          // setLocationName(`${response.data.label}`);

          if (!response || !response.data) {
            navigate("/404");
            return;
          }

          const location = response.data;

          console.log("Location data:", location);

          if (
            (location.clientId === "METROLINX" ||
              location.clientId === "Metrolinx" ||
              location.clientId === "metrolinx") &&
            !isMetroLinxClient
          ) {
            setIsMetroLinxClient(true);
          }

          if (
            String(location.clientId || "")
              .trim()
              .toUpperCase() === "CITYMARKHAM"
          ) {
            setIsCityOfMarkhamClient(true);
          }

          // Check if washrooms exist and find the washroom by id
          if (!location.washrooms) {
            navigate("/404");
            return;
          }

          let washroomExists = false;
          for (const washroom of Object.values(location.washrooms)) {
            if (washroom.id === urlparams.washroomId) {
              washroomExists = true;
              break;
            }
          }

          if (location && location.position) {
            validateDistance(userLocation, location.position);
          }

          if (!washroomExists) {
            navigate("/404");
            return;
          }
        } catch (error) {
          console.error("Error validating washroom:", error);
          navigate("/404");
        }
        setLoading(false);
      } else if (urlparams?.locationId || urlparams?.washroomId) {
        // If only one parameter is present, navigate to 404
        navigate("/404");
      }
    };

    validateWashroom();
  }, [urlparams?.locationId, urlparams?.washroomId, navigate, userLocation]);

  const onSubmit = handleSubmit(async (data) => {
    setLabelMessage("");
    setLoading(true);
    try {
      if (urlparams?.locationId) {
        data.locationId = urlparams.locationId;
      }
      data.cleaningDate = moment().format("YYYY-MM-DD");
      data.startTime = moment().format("YYYY-MM-DDTHH:mm:ss");
      delete data.cleaningStartDate;
      if (employeeData) {
        data.employeeId = employeeData.id || data.employeeId;
      }
      data.washroomId = urlparams?.washroomId;
      console.log("Submitted data:", data);
      const { status, message } = await FirebaseDataService.createWashroomLogs(
        data as LocationSpecificWashroomLogsForTable,
      );
      if (status === "success") {
        setLabelMessage("Washroom log submitted successfully.");
        setSubmitSuccess(true);
        reset();
        setEmployeeData(null);
      } else {
        throw new Error(message || "Error submitting washroom log");
      }
    } catch (error) {
      setError(getErrorMsg(error));
      debugError(error);
    }

    setLoading(false);
  });

  const fetchAndValidateEmployee = useCallback(
    async (employeeId: string) => {
      if (!employeeId) {
        setValidationError("Employee ID is required");
        return;
      }
      setValidationError("");

      try {
        const response = await FirebaseDataService.getEmployeeById(employeeId);

        if (response && response.data) {
          setEmployeeData(response.data);
          setValidationError("");
          // Optionally populate first name and last name fields
          setValue("firstName", response.data.firstName || "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue("lastName", response.data.lastName || "", {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          setEmployeeData(null);
          setValidationError("Not a valid employee ID");
        }
      } catch (error) {
        console.error("Employee validation error:", error);
        setEmployeeData(null);
        setValidationError("Error validating employee ID");
        setValue("firstName", "");
        setValue("lastName", "");
      }
    },
    [setValue],
  );

  const getData = async (code = "") => {
    setLoading(true);
    try {
      const { data } = await FirebaseDataService.getClientDataForNoAuthMode(urlparams.clientCode || code);
      if (!data) {
        // navigate("/404");
      }
      const { clientData, maximoTimezone } = data;
      dispatch(logInUser({ clientId: clientData.clientId, maximoTimezone: maximoTimezone || MAXIMO_TIME_ZONE }));
      setLabelMessage("Please enter your first and last name");
      if (clientData.clientId.toLowerCase() === "metrolinx") {
        setIsMetroLinxClient(false);
      }

      if (
        String(clientData.clientId || "")
          .trim()
          .toUpperCase() === "CITYMARKHAM"
      ) {
        setIsCityOfMarkhamClient(true);
      }

      if (clientData?.name) {
        setName(clientData.name);
      }
      if (clientData.logo) {
        try {
          const { data: logoUrl } = await FirebaseStorageService.getDownloadURL(
            process.env.REACT_APP_CLIENT_LOGO_FOLDER,
            clientData.logo,
          );
          setLogo(logoUrl);
        } catch (error) {
          // intentionally left empty as there is no handling required for a missing Client logo
        }
      }
    } catch (error) {
      debugError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData(urlparams.clientCode || "");
  }, []);

  const handleEmployeeIdValidation = useCallback(
    (employeeId: string) => {
      if (employeeId && employeeId.length > 0) {
        fetchAndValidateEmployee(employeeId);
      } else {
        setEmployeeData(null);
        setValidationError("");
      }
    },
    [fetchAndValidateEmployee],
  );

  return (
    <Container>
      {loading ? (
        <EmptyContainer>
          <Spinner animation="border" />
        </EmptyContainer>
      ) : (
        <>
          <Header name={name} logo={logo} noLink noUserLoginPage />
          <WashroomLogsInputForm
            key={0}
            locationName={locationName}
            onSubmit={onSubmit}
            register={register}
            isValid={isValid}
            loading={loading}
            error={error}
            errors={errors}
            labelMessage={labelMessage}
            validateEmployeeId={handleEmployeeIdValidation}
            validationError={validationError}
            setValue={setValue}
            trigger={trigger}
            submitSuccess={submitSuccess}
            isMetroLinxClient={isMetroLinxClient}
            isCityOfMarkhamClient={isCityOfMarkhamClient}
          />
          <StickyFooter />
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  background-color: ${colors.white.default};
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: 60px;
`;

const EmptyContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StickyFooter = styled(Footer)`
  position: sticky;
  margin-top: auto;
`;
